import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

function buildQuery(searchParams: URLSearchParams): Record<string, string | string[] | undefined> {
  const result: Record<string, string | string[] | undefined> = {};
  searchParams.forEach((_, key) => {
    const all = searchParams.getAll(key);
    result[key] = all.length === 1 ? all[0] : all;
  });
  return result;
}

type RouterUrl = string | { pathname: string; query?: Record<string, string | string[]> };

function resolveUrl(url: RouterUrl): string {
  if (typeof url === 'string') return url;
  const params = new URLSearchParams();
  Object.entries(url.query || {}).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach(val => params.append(k, val));
    else params.set(k, v);
  });
  const qs = params.toString();
  return `${url.pathname}${qs ? '?' + qs : ''}`;
}

/**
 * Compatibility shim replacing Next.js useSafeRouter for React Router.
 * router.isReady is always true since React Router params are synchronous.
 */
export function useSafeRouter() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return {
    isReady: true,
    query: buildQuery(searchParams),
    pathname: location.pathname,
    push: (url: RouterUrl) => navigate(resolveUrl(url)),
    replace: (url: RouterUrl, _as?: unknown, _opts?: { shallow?: boolean }) =>
      navigate(resolveUrl(url), { replace: true }),
  };
}

export function useSafeQuery<T extends Record<string, string | string[] | undefined>>(): Partial<T> {
  const [searchParams] = useSearchParams();
  return buildQuery(searchParams) as Partial<T>;
}

export function useSafePathname(): string {
  return useLocation().pathname;
}
